import { FieldName } from "@/components/custom/AlternativeTransportation/Transportations/ManualTransports/ManualTransportInput/fields";
import { useFormContext } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { TransportType } from "@/core/types/types";
import TransportInputField from "@/components/custom/AlternativeTransportation/Transportations/ManualTransports/ManualTransportInput/TransportInputField";
import { Box, Theme, useTheme } from "@mui/system";
import { CustomizedButtonProps } from "@flightright/reusable-components";
import { requiredFieldsArePresent } from "@/core/utils/requiredFieldsValidator";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { Button, CircularProgress, Typography } from "@mui/material";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { validateAirlineByCode } from "@/api/airlinesApi";
import { flightApi } from "@/api/flightApi";
import { determineAirlineCode } from "@/core/utils/determineAirlineCode";

interface ManualTransportInputProps {
  transportOrder: number;
  transportType: TransportType;
  onTransportEnter: (transport: any) => void;
  appendedToFlightsList: boolean;
  disableFutureDates?: boolean;
  selectedFlightDate?: string;
  originAirportData: string;
  destinationAirportData: string;
}

const DEFAULT_TRANSPORT_FIELDS = [
  FieldName.ManualDepartureLabel,
  FieldName.ManualDepartureDateTime,
  FieldName.ManualArrivalLabel,
  FieldName.ManualArrivalDateTime,
];

const TRANSPORT_FIELDS: Record<TransportType, FieldName[]> = {
  [TransportType.plane]: [
    FieldName.FlightPickerHint,
    FieldName.ManualFlightNumber,
    FieldName.ManualDepartureLabel,
    FieldName.ManualDepartureDateTime,
    FieldName.ManualArrivalLabel,
    FieldName.ManualArrivalDateTime,
    FieldName.FlightPickerAccordion,
  ],
  [TransportType.bus]: DEFAULT_TRANSPORT_FIELDS,
  [TransportType.train]: DEFAULT_TRANSPORT_FIELDS,
};

const styles = (theme: Theme) => ({
  [FieldName.FlightPickerAccordion]: {
    marginTop: theme.spacing(2),
  },
  [FieldName.FlightPickerHint]: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  [FieldName.ManualFlightNumber]: {
    marginBottom: theme.spacing(2),
  },
  [FieldName.ManualDepartureLabel]: {
    "& .MuiTypography-root": {
      fontWeight: 700,
    },
  },
  [FieldName.ManualArrivalLabel]: {
    marginTop: theme.spacing(3),
    "& .MuiTypography-root": {
      fontWeight: 700,
    },
  },
});

export const MANUAL_TRANSPORTS_FIELD_KEY = "manualTransports";

const ManualTransportInput = ({
  transportOrder,
  onTransportEnter,
  transportType,
  appendedToFlightsList,
  disableFutureDates,
  selectedFlightDate,
  originAirportData,
  destinationAirportData,
}: ManualTransportInputProps) => {
  const { watch, trigger, setError } = useFormContext();
  const formValues = watch();
  const [saveButtonColor, setSaveButtonColor] =
    useState<CustomizedButtonProps["color"]>("inherit");
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const classes: any = useCustomStyles(styles);
  const theme = useTheme();
  const invalidFlightNumberTranslation = t(
    "validate.manualFlightNumber.checkFlightNumber.message"
  );
  const invalidAirportTranslation = t(
    "validate.manualFlightNumber.verifyAirportByFlightNumber.message"
  );
  const handleSave = async () => {
    // validate fields only for current transport, and not the whole form
    const inputValues = formValues[MANUAL_TRANSPORTS_FIELD_KEY][transportOrder];
    const fieldKeysForValidation = Object.keys(inputValues).map(
      (fieldKey) =>
        `${MANUAL_TRANSPORTS_FIELD_KEY}[${transportOrder}]${fieldKey}`
    );

    trigger(fieldKeysForValidation).then(async (isValid: boolean) => {
      if (!isValid) return;

      if (transportType === TransportType.plane) {
        const airlineUppercase: string =
          inputValues[FieldName.ManualFlightNumber].toUpperCase();
        const airlineCode: string =
          determineAirlineCode(airlineUppercase) ?? "";

        const manualFlightNumberKey = `${MANUAL_TRANSPORTS_FIELD_KEY}[${transportOrder}]${FieldName.ManualFlightNumber}`;
        if (fieldKeysForValidation.includes(manualFlightNumberKey)) {
          setIsButtonLoading(true);
          const dateToSearch: string =
            inputValues[FieldName.ManualDepartureDateTime].split("T")[0];

          try {
            const { isValid: airlineStatus } = await validateAirlineByCode(
              airlineCode
            );

            const { status: airportStatus } =
              dateToSearch &&
              (await flightApi.verifyAirportByFlightNumber({
                departureDate: dateToSearch,
                flightNumber: airlineUppercase,
                departureAirport: originAirportData,
                arrivalAirport: destinationAirportData,
              }));
            if (!airlineStatus) {
              setError(
                `${MANUAL_TRANSPORTS_FIELD_KEY}[${transportOrder}].manualFlightNumber`,
                {
                  type: "manual",
                  message: invalidFlightNumberTranslation,
                }
              );
              return;
            }
            if (!airportStatus) {
              setError(
                `${MANUAL_TRANSPORTS_FIELD_KEY}[${transportOrder}].manualFlightNumber`,
                {
                  type: "manual",
                  message: invalidAirportTranslation,
                }
              );
              return;
            }
          } catch (error) {
            console.error(
              "Error occurred during airline code validation: ",
              error
            );
          } finally {
            setIsButtonLoading(false);
          }
        }
      }
      onTransportEnter({
        transportTypeName: transportType,
        flightNumber: inputValues[FieldName.ManualFlightNumber],
        transportRouteOrder: transportOrder,
        scheduledDepartureTime: inputValues[FieldName.ManualDepartureDateTime],
        scheduledArrivalTime: inputValues[FieldName.ManualArrivalDateTime],
      });
    });
  };

  // Monitoring form values to set the correct submit button color value
  useEffect(() => {
    // Using setTimeout, because control._fields gets updated at the end of an event loop (and not along with formValues)
    setTimeout(() => {
      const manualTransports = formValues[MANUAL_TRANSPORTS_FIELD_KEY];
      if (!manualTransports) return;

      // Consider only current transport fields
      const currentTransportFields = Object.values(
        formValues[MANUAL_TRANSPORTS_FIELD_KEY][transportOrder]
      );
      const inputValues = {
        [MANUAL_TRANSPORTS_FIELD_KEY]: currentTransportFields,
      };
      const requiredFieldsAreFilled = requiredFieldsArePresent(
        null,
        inputValues,
        MANUAL_TRANSPORTS_FIELD_KEY
      );
      const newButtonColor = requiredFieldsAreFilled ? undefined : "inherit";

      setSaveButtonColor(newButtonColor);
    }, 50);
  }, [formValues]);

  const renderInputFields = (fields: FieldName[]) => (
    <Box mt={appendedToFlightsList ? 4 : 0}>
      {fields.map((fieldName: FieldName) => {
        const defaultValue =
          fieldName == FieldName.ManualDepartureDateTime ||
          fieldName == FieldName.ManualArrivalDateTime
            ? selectedFlightDate
            : undefined;

        return (
          <Box key={fieldName} sx={classes[fieldName]}>
            <TransportInputField
              fieldName={fieldName}
              transportOrder={transportOrder}
              defaultValue={defaultValue}
              disableFutureDates={disableFutureDates}
            />
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      {renderInputFields(TRANSPORT_FIELDS[transportType])}

      <Button
        sx={{ width: "100%", marginTop: theme.spacing(4) }}
        className="navigation"
        variant="contained"
        color={saveButtonColor}
        onClick={handleSave}
        disabled={isButtonLoading}
      >
        {isButtonLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Typography>
            {t("altTransport.itinerary.saveButton.label")}
          </Typography>
        )}
      </Button>
    </>
  );
};

export default ManualTransportInput;
